export interface AllocationBySecurity {
  code: string;
  name: string;
  security: {
    id: number;
    isinCode: string;
    countryCode: string;
    currencyCode: string;
    tagsAsList: string[];
  };
  firstAnalysis: {
    marketValue: number;
    tradeAmount: number;
    fxRate: number;
    amount: number;
    marketUnitPriceDate: string;
    purchaseTradeAmount: number;
    twr: number;
    accrual: number;
  };
}

export interface AllocationByType {
  code: SecurityTypeCode;
  name: string;
  firstAnalysis: {
    marketValue: number;
    tradeAmount: number;
    twr: number;
  };
  allocationsBySecurity: AllocationBySecurity[];
}

export interface AllPortfoliosHoldingsQuery {
  contact: {
    analytics: {
      allocationTopLevel: {
        portfolio: {
          currencyCode: string;
        };
        allocationByType: AllocationByType[];
      };
    };
  };
}

export interface PortfolioHoldingsQuery {
  portfolio: {
    analytics: {
      allocationTopLevel: {
        portfolio: {
          currencyCode: string;
        };
        allocationByType: AllocationByType[];
      };
    };
  };
}
export interface HoldingsQuery {
  analytics: {
    allocationTopLevel: {
      portfolio: {
        currencyCode: string;
      };
      allocationByType: AllocationByType[];
    };
  };
}

export interface MarketHistoryDataPoint {
  price: number;
  date: string;
}

export interface SecurityDetailsPosition {
  id: number;
  name: string;
  securityCode: string;
  isinCode: string;
  url: string;
  url2: string;
  currency: {
    securityCode: string;
  };
  latestMarketData?: {
    price: number;
    date: string;
  };
  type: {
    code: SecurityTypeCode;
    namesAsMap: Record<string, string>;
    name: string;
  };
  securitySubType?: {
    code: SecuritySubTypeCode;
    namesAsMap: Record<string, string>;
    name: string;
  };
  fxRate: number;
  // misnamed on backend, should be tagsAsList
  tagsAsSet: string[];
  documents: {
    fileName: string;
    identifier: string;
    mimeType: string;
  }[];
  profileDataString: string;
  profile: {
    attributes: {
      attributeKey: string;
      defaultValue: string | number;
    }[];
  };
}

export interface SecurityDetailsQuery {
  security: SecurityDetailsPosition;
}

export interface SecurityMarketDataHistory {
  marketDataHistory: MarketHistoryDataPoint[];
}

export interface SecurityMarketDataHistoryQuery {
  security: SecurityMarketDataHistory;
}

export interface HoldingPosition {
  security: {
    id: number;
  };
  amount: number;
  purchaseTradeAmount: number;
  marketValue: number;
  marketFxRate: number;
  amountFromOpenTradeOrders: number;
  amountFromSentTradeOrders: number;
  tradeAmountFromOpenTradeOrders: number;
  tradeAmountFromSentTradeOrders: number;
}

export interface UnpaidTrades {
  id: number;
  security: {
    id: number;
  };
  type: {
    typeCode: string;
    amountEffect: number;
  };
  amount: number;
  tradeAmount: number;
}


export interface PortfolioHoldingDetailsQuery {
  portfolio: {
    portfolioReport: {
      holdingPositions: HoldingPosition[];
    };
    unpaidPortfolioReport: {
      unpaidTransactions: UnpaidTrades[];
    };
  };
}

export interface MarketHistoryDataPoint {
  price: number;
  date: string;
}

/** Standard solution Security type codes.*/
export enum SecurityTypeCode {
  EQUITY = "E", //Equity
  ETF = "CE", //Exchange-traded funds (ETFs)
  PRIVATE_EQUITY = "PE", //Private Equity
  DEBT_INSTRUMENT = "D", //Debt instruments
  FUND = "FUND", // Alfred Berg Funds
  COLLECTIVE_INVESTMENT_VEHICLE = "C", //Collective investment vehicles
  CURRENCY = "TC", //Currencies
}

/** Alfred Berg Fund Subtypes */

export enum SecuritySubTypeCode {
  EQUITY_FUND = "EF", //Equity Fund
  BOND_FUND = "BF", //Bond Fund
  MONEY_MARKET_FUND = "MMF", //Money Market Fund
  HEDGE_FUND = "HF", //Hedge Fund
}

/**
 * Security tags indicating whether a security can be bought or sold
 * in units and/or trade amount.
 */
export enum SecurityTradeType {
  buyUnits = "Trade type:Buy units",
  buyTradeAmount = "Trade type:Buy trade amount",
  sellUnits = "Trade type:Sell units",
  sellTradeAmount = "Trade type:Sell trade amount",
}

interface KIIDType {
  KIID?: {
    date: string;
    value: string;
  };
}

interface GenericKeyFigures {
  [key: string]: {
    date: string;
    value: string | number;
  };
}

export type KeyFigures = KIIDType & GenericKeyFigures;

export interface SecurityDetailsPosition {
  id: number;
  name: string;
  securityCode: string;
  isinCode: string;
  url: string;
  url2: string;
  currency: {
    securityCode: string;
  };
  latestMarketData?: {
    price: number;
    date: string;
  };
  type: {
    code: SecurityTypeCode;
    namesAsMap: Record<string, string>;
    name: string;
  };
  fxRate: number;
  // misnamed on backend, should be tagsAsList
  tagsAsSet: string[];
  minTradeAmount: number;
  figuresAsObject: {
    latestValues: KeyFigures;
  };
  documents: {
    fileName: string;
    identifier: string;
    mimeType: string;
  }[];
}

export interface SecurityDetailsQuery {
  security: SecurityDetailsPosition;
}

export interface SecurityMarketDataHistory {
  marketDataHistory: MarketHistoryDataPoint[];
}

export interface SecurityMarketDataHistoryQuery {
  security: SecurityMarketDataHistory;
}

export interface HoldingPosition {
  security: {
    id: number;
  };
  amount: number;
  purchaseTradeAmount: number;
  marketValue: number;
  marketFxRate: number;
  amountFromOpenTradeOrders: number;
  amountFromSentTradeOrders: number;
  tradeAmountFromOpenTradeOrders: number;
  tradeAmountFromSentTradeOrders: number
}

export interface PortfolioHoldingDetailsQuery {
  portfolio: {
    portfolioReport: {
      holdingPositions: HoldingPosition[];
      
    };
    unpaidPortfolioReport: {
      unpaidTransactions: UnpaidTrades[];
    };
  };
}
